// components/common/MetricDetailsModal.jsx - MOBILE RESPONSIVE VERSION

import React, { useState } from 'react';
import { XMarkIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { formatCurrency } from '../../utils';

const MetricDetailsModal = ({ 
  title, 
  monthlyData, 
  isOpen, 
  onClose, 
  dataType, 
  showTotal = true,
  showProjected = true,
  tooltipLabels = null
}) => {
  const [showMobileLegend, setShowMobileLegend] = useState(false);

  if (!isOpen) return null;

  const formatValue = (value) => {
    if (dataType === 'currency') return formatCurrency(value);
    if (dataType === 'percentage') return `${value.toFixed(1)}%`;
    if (dataType === 'rating') return `${value.toFixed(1)}/5`;
    if (dataType === 'number') return Math.round(value).toString();
    return value.toFixed(1);
  };

  if (!monthlyData || monthlyData.length === 0) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full">
          <div className="bg-gray-50 px-4 sm:px-6 py-3 sm:py-4 border-b flex items-center justify-between">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900">{title}</h2>
            <button onClick={onClose}><XMarkIcon className="h-5 w-5 sm:h-6 sm:w-6" /></button>
          </div>
          <div className="p-4 sm:p-6"><p className="text-gray-500 text-sm">No data available</p></div>
        </div>
      </div>
    );
  }

  // ✅ Create full 12-month template
  const create12MonthTemplate = () => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const year = monthlyData[0]?.period_label?.split(' ')[1] || new Date().getFullYear();
    
    return months.map((month, index) => ({
      period_label: `${month} ${year}`,
      value: 0,
      count: 0,
      total: 0,
      month_num: index + 1,
      hasData: false
    }));
  };

  // ✅ Merge actual data into 12-month template
  const fullYearData = create12MonthTemplate();
  
  monthlyData.forEach(dataPoint => {
    const [monthName, yearStr] = dataPoint.period_label.split(' ');
    const monthIndex = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
      .findIndex(m => monthName.startsWith(m));
    
    if (monthIndex !== -1) {
      fullYearData[monthIndex] = {
        ...dataPoint,
        hasData: true,
        month_num: monthIndex + 1
      };
    }
  });

  const chartData = fullYearData.map((month, index) => {
    const previousMonth = index > 0 ? fullYearData[index - 1] : null;
    const change = previousMonth && previousMonth.value !== 0 
      ? ((month.value - previousMonth.value) / Math.abs(previousMonth.value)) * 100 
      : 0;
    
    const [monthName, yearStr] = month.period_label.split(' ');
    const monthDate = new Date(`${monthName} 1, ${yearStr}`);
    const today = new Date();
    const currentMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    
    const isFuture = monthDate > currentMonth;
    
    return { 
      month: month.period_label, 
      value: month.value || 0,
      change,
      isFuture,
      count: month.count,
      total: month.total,
      hasData: month.hasData
    };
  });

  const values = chartData
    .filter(d => d.hasData && (!d.isFuture || showProjected))
    .map(d => d.value);
  const maxValue = Math.max(...values, 0);
  const minValue = Math.min(...values, 0);
  
  const calculateTickMarks = (min, max) => {
    const absMax = Math.max(Math.abs(min), Math.abs(max));
    let tickInterval;
    
    if (absMax <= 10) tickInterval = 2;
    else if (absMax <= 50) tickInterval = 10;
    else if (absMax <= 100) tickInterval = 20;
    else if (absMax <= 500) tickInterval = 100;
    else if (absMax <= 1000) tickInterval = 200;
    else if (absMax <= 5000) tickInterval = 1000;
    else if (absMax <= 10000) tickInterval = 2000;
    else if (absMax <= 50000) tickInterval = 10000;
    else if (absMax <= 100000) tickInterval = 20000;
    else if (absMax <= 500000) tickInterval = 50000;
    else tickInterval = 100000;
    
    const minTick = Math.floor(min / tickInterval) * tickInterval;
    const maxTick = Math.ceil(max / tickInterval) * tickInterval;
    
    const ticks = [];
    for (let i = minTick; i <= maxTick; i += tickInterval) {
      ticks.push(i);
    }
    
    if (ticks.length > 10) {
      const newTicks = [];
      for (let i = minTick; i <= maxTick; i += tickInterval * 2) {
        newTicks.push(i);
      }
      return { ticks: newTicks, minTick, maxTick };
    }
    
    return { ticks, minTick, maxTick };
  };
  
  const { ticks, minTick, maxTick } = calculateTickMarks(minValue, maxValue);
  const actualRange = maxTick - minTick;
  
  const chartHeight = 370;
  const zeroLineBottomPx = ((0 - minTick) / actualRange) * chartHeight;

  const formatBreakdownValue = (value, isCount) => {
    if (typeof value === 'number' && value > 1000) {
      return `$${value.toLocaleString()}`;
    }
    return value;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
        {/* Header - RESPONSIVE */}
        <div className="bg-gray-50 px-3 sm:px-6 py-3 sm:py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-base sm:text-xl font-semibold text-gray-900 truncate pr-2">
            {title} <span className="hidden sm:inline">- Monthly Breakdown</span>
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 flex-shrink-0">
            <XMarkIcon className="h-5 w-5 sm:h-6 sm:w-6" />
          </button>
        </div>

        <div className="p-3 sm:p-6 overflow-y-auto max-h-[calc(95vh-120px)] sm:max-h-[calc(90vh-140px)]">
          {/* Legend - RESPONSIVE */}
          {showProjected && (
            <div className="relative mb-4">
              {/* Desktop Legend */}
              <div className="hidden sm:flex items-center justify-center gap-6">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-teal-500 rounded"></div>
                  <span className="text-sm text-gray-700 font-medium">Actual</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-violet-200 rounded"></div>
                  <span className="text-sm text-gray-700 font-medium">Projected</span>
                </div>
              </div>

              {/* Mobile Legend Toggle */}
              <div className="sm:hidden flex justify-end">
                <button
                  onClick={() => setShowMobileLegend(!showMobileLegend)}
                  className="bg-gray-800 bg-opacity-75 text-white px-3 py-1.5 rounded-md text-xs font-medium flex items-center gap-1.5"
                >
                  <InformationCircleIcon className="w-4 h-4" />
                  Legend
                </button>
              </div>

              {/* Mobile Legend Popup */}
              {showMobileLegend && (
                <div className="sm:hidden absolute top-10 right-0 bg-white shadow-xl rounded-lg p-3 border border-gray-200 z-10">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 bg-teal-500 rounded"></div>
                      <span className="text-xs text-gray-700 font-medium">Actual</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 bg-violet-200 rounded"></div>
                      <span className="text-xs text-gray-700 font-medium">Projected</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Chart Container - RESPONSIVE WITH HORIZONTAL SCROLL */}
          <div className="bg-gray-50 p-2 sm:p-6 rounded-lg overflow-x-auto">
            <div className="flex gap-2 sm:gap-3 relative min-w-[800px]" style={{ height: '420px', paddingBottom: '50px', paddingTop: '40px' }}>
              {/* Y-axis labels - RESPONSIVE */}
              <div className="flex flex-col justify-between" style={{ width: '45px', height: '370px' }}>
                {ticks.slice().reverse().map((tick, index) => {
                  const isZero = tick === 0;
                  return (
                    <div 
                      key={index} 
                      className={`text-[10px] sm:text-xs text-right ${isZero ? 'text-gray-900 font-semibold' : 'text-gray-600'}`}
                    >
                      {formatValue(tick)}
                    </div>
                  );
                })}
              </div>
              
              {/* Chart area - REDUCED GAPS */}
              <div className="flex-1 flex items-end justify-between gap-1 sm:gap-2 relative overflow-visible" style={{ height: '370px' }}>
                
                {/* Y-axis line */}
                <div className="absolute left-0 top-0 bottom-0 border-l-2 border-gray-400 z-10" />
                
                {/* Y-axis tick marks */}
                {ticks.map((tick, index) => {
                  const tickPosition = ((tick - minTick) / actualRange) * chartHeight;
                  return (
                    <div
                      key={`tick-${index}`}
                      className="absolute left-0 w-2 border-t-2 border-gray-400 z-10"
                      style={{ bottom: `${tickPosition}px` }}
                    />
                  );
                })}
                
                {/* Zero Line */}
                <div 
                  className="absolute left-0 right-0 border-t-2 border-gray-900 z-20"
                  style={{ bottom: `${zeroLineBottomPx}px` }}
                />
              
              {/* YTD Average Line */}
              {(() => {
                const actualData = chartData.filter(d => d.hasData && !d.isFuture);
                if (actualData.length > 0) {
                  const totValue = actualData.reduce((sum, d) => sum + d.value, 0);
                  const avgValue = totValue / actualData.length;
                  const avgHeightPx = ((avgValue - minTick) / actualRange) * chartHeight;
                  const bottomPosition = avgHeightPx;
                  
                  return (
                    <>
                      <div 
                        className="absolute left-0 right-0 border-t-2 border-gray-300 border-dashed z-10"
                        style={{ bottom: `${bottomPosition}px` }}
                      />
                      <div 
                        className="absolute right-2 bg-white text-gray-700 text-[10px] sm:text-xs px-2 py-1 rounded shadow-sm z-10"
                        style={{ bottom: `${bottomPosition - 12}px` }}
                      >
                        <div className="whitespace-nowrap">YTD Avg: {formatValue(avgValue)}</div>
                        {showTotal && <div className="whitespace-nowrap hidden sm:block">YTD Total: {formatValue(totValue)}</div>}
                      </div>
                    </>
                  );
                }
                return null;
              })()}
              
              {/* ALL 12 MONTHS */}
              {chartData.map((data, index) => {
                const isNegative = data.value < 0;
                const isPositiveChange = data.change > 0;
                const isNegativeChange = data.change < 0;
                
                const valuePosition = ((data.value - minTick) / actualRange) * chartHeight;
                const barHeightPx = Math.abs(valuePosition - zeroLineBottomPx);
                
                const shouldShowBar = data.hasData && (!data.isFuture || showProjected) && data.value !== 0;
                
                return (
                  <div key={index} className="flex-1 relative overflow-visible" style={{ height: '370px' }}>
                    {/* Positive bar */}
                    {!isNegative && shouldShowBar && (
                      <div 
                        className="absolute left-0 right-0 group overflow-visible"
                        style={{ 
                          bottom: `${zeroLineBottomPx}px`,
                          height: `${barHeightPx}px`,
                        }}
                      >
                        {/* Tooltip - RESPONSIVE */}
                        <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 -translate-y-full bg-gray-900 text-white text-[10px] sm:text-xs rounded py-2 px-2 sm:px-3 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 shadow-lg">
                          <div className="font-semibold mb-1">{data.month}</div>
                          <div className="mb-1">{formatValue(data.value)}</div>
                          
                          {data.count !== undefined && data.total !== undefined && tooltipLabels && (
                            <div className="text-gray-300 text-[10px] mt-2 pt-2 border-t border-gray-700 space-y-1">
                              <div className="flex justify-between gap-2 sm:gap-4">
                                <span>{tooltipLabels.countLabel}:</span>
                                <span className="font-semibold">{formatBreakdownValue(data.count, true)}</span>
                              </div>
                              <div className="flex justify-between gap-2 sm:gap-4">
                                <span>{tooltipLabels.totalLabel}:</span>
                                <span className="font-semibold">{formatBreakdownValue(data.total, false)}</span>
                              </div>
                            </div>
                          )}
                          
                          {dataType === 'percentage' && data.count !== undefined && data.total !== undefined && !tooltipLabels && (
                            <div className="text-gray-300 text-[10px]">
                              ({data.count} of {data.total})
                            </div>
                          )}
                          
                          {index > 0 && data.hasData && (
                            <div className={`${isPositiveChange ? 'text-green-400' : isNegativeChange ? 'text-red-400' : 'text-gray-400'} mt-1 text-[10px]`}>
                              {isPositiveChange && '+'}{data.change.toFixed(1)}% from prev
                            </div>
                          )}
                          {data.isFuture && (
                            <div className="text-blue-400 text-[10px]">Projected</div>
                          )}
                        </div>
                        
                        <div 
                          className={`w-full h-full rounded-t transition-colors cursor-pointer flex items-center justify-center ${
                            data.isFuture 
                              ? 'bg-violet-200 hover:bg-violet-300' 
                              : 'bg-teal-500 hover:bg-teal-600'
                          }`}
                        >
                          <span className="text-white text-[9px] sm:text-xs font-semibold">
                            {formatValue(data.value)}
                          </span>
                        </div>
                      </div>
                    )}
                    
                    {/* Negative bar */}
                    {isNegative && shouldShowBar && (
                      <div 
                        className="absolute left-0 right-0 group overflow-visible"
                        style={{ 
                          bottom: `${zeroLineBottomPx - barHeightPx}px`,
                          height: `${barHeightPx}px`,
                        }}
                      >
                        <div className="absolute top-full mt-2 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-[10px] sm:text-xs rounded py-2 px-2 sm:px-3 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 shadow-lg">
                          <div className="font-semibold mb-1">{data.month}</div>
                          <div className="mb-1">{formatValue(data.value)}</div>
                          
                          {data.count !== undefined && data.total !== undefined && tooltipLabels && (
                            <div className="text-gray-300 text-[10px] mt-2 pt-2 border-t border-gray-700 space-y-1">
                              <div className="flex justify-between gap-2 sm:gap-4">
                                <span>{tooltipLabels.countLabel}:</span>
                                <span className="font-semibold">{formatBreakdownValue(data.count, true)}</span>
                              </div>
                              <div className="flex justify-between gap-2 sm:gap-4">
                                <span>{tooltipLabels.totalLabel}:</span>
                                <span className="font-semibold">{formatBreakdownValue(data.total, false)}</span>
                              </div>
                            </div>
                          )}
                          
                          {dataType === 'percentage' && data.count !== undefined && data.total !== undefined && !tooltipLabels && (
                            <div className="text-gray-300 text-[10px]">
                              ({data.count} of {data.total})
                            </div>
                          )}
                          
                          {index > 0 && data.hasData && (
                            <div className={`${isPositiveChange ? 'text-green-400' : isNegativeChange ? 'text-red-400' : 'text-gray-400'} mt-1 text-[10px]`}>
                              {isPositiveChange && '+'}{data.change.toFixed(1)}% from prev
                            </div>
                          )}
                          {data.isFuture && (
                            <div className="text-blue-400 text-[10px]">Projected</div>
                          )}
                        </div>
                        
                        <div 
                          className={`w-full h-full rounded-b transition-colors cursor-pointer flex items-center justify-center ${
                            data.isFuture 
                              ? 'bg-pink-200 hover:bg-pink-300' 
                              : 'bg-pink-500 hover:bg-pink-600'
                          }`}
                        >
                          <span className="text-white text-[9px] sm:text-xs font-semibold">
                            {formatValue(data.value)}
                          </span>
                        </div>
                      </div>
                    )}
                    
                    {/* X-axis label - ALWAYS SHOWN */}
                    <div 
                      className="absolute left-1/2 text-[9px] sm:text-xs text-gray-600 text-left font-medium origin-top-left whitespace-nowrap" 
                      style={{ 
                        bottom: '-50px',
                        transform: 'translateX(-50%) rotate(-45deg) translateY(10px)'
                      }}
                    >
                      {data.month.split(' ')[0]}
                    </div>
                  </div>
                );
              })}
              </div>
            </div>
          </div>
        </div>

        {/* Footer - RESPONSIVE */}
        <div className="bg-gray-50 px-3 sm:px-6 py-3 sm:py-4 border-t flex justify-end">
          <button onClick={onClose} className="px-3 sm:px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 text-sm">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default MetricDetailsModal;